import Axios from "axios";
import { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";

const Suppliers = () => {

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [editSupplier, setEditSupplier] = useState(null);

  const [newSupplier, setNewSupplier] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  // Fetch suppliers
  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await Axios.get("https://store.petplanet.rw/api/suppliers");
      setData(res.data || []);
    } catch (error) {
      console.error("Error fetching suppliers", error);
    } finally {
      setLoading(false);
    }
  };

  // Add Supplier
  const handleAddSupplier = async () => {
    try {
      await Axios.post("https://store.petplanet.rw/api/suppliers", newSupplier);

      alert("Supplier added successfully!");

      setIsAddModalOpen(false);
      setNewSupplier({
        name: "",
        email: "",
        phone: "",
        address: "",
      });

      fetchSuppliers();
    } catch (error) {
      console.error("Error adding supplier", error);
    }
  };

  // Update Supplier
  const handleSave = async () => {
    try {
      await Axios.put(
        `https://store.petplanet.rw/api/suppliers/${editSupplier.id}`,
        editSupplier
      );

      alert("Supplier updated successfully!");

      setIsEditModalOpen(false);
      fetchSuppliers();
    } catch (error) {
      console.error("Error updating supplier", error);
    }
  };

  // Delete Supplier
  const handleDelete = async (id) => {

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this supplier?"
    );

    if (!confirmDelete) return;

    try {
      await Axios.delete(`https://store.petplanet.rw/api/suppliers/${id}`);

      setData((prev) => prev.filter((item) => item.id !== id));

      alert("Supplier deleted successfully!");
    } catch (error) {
      console.error("Error deleting supplier", error);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Supplier Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      renderCell: (params) => (
        <Box display="flex">
          <IconButton
            color="secondary"
            onClick={() => {
              setEditSupplier(params.row);
              setIsEditModalOpen(true);
            }}
          >
            <EditOutlinedIcon />
          </IconButton>

          <IconButton
            style={{ color: "#FF0000" }}
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box m="20px" mt="80px">

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Suppliers" subtitle="Manage Suppliers" />

          <IconButton
            onClick={() => setIsAddModalOpen(true)}
           sx={{
            fontSize: "13px",
            backgroundColor: tokens.redAccent[400],
            border: "none",
            color: tokens.grey[100],
            padding: "14px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            marginTop: "-13px",
            transition: "all 0.3s ease",
            "&:hover": {
              outline: `2px solid ${tokens.redAccent[400]}`,
              outlineOffset: "1px",
              backgroundColor: tokens.redAccent[200],
            },
            "&:active": {
              outline: `2px solid ${tokens.redAccent[500]}`,
              transform: "scale(0.97)",
            },
          }}
          >
            Add Supplier <AddOutlinedIcon />
          </IconButton>
        </Box>

        <Box height="75vh" mt="20px"
          sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: tokens.grey[800],
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: tokens.grey[100],
          },
          "& .MuiCheckbox-root": {
            border: "none",
            color: `${tokens.redAccent[400]} !important`,
          },
          "& .MuiDataGrid-columnHeader.action-column--header": {
            background: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: `${tokens.grey[900]} !important`,
            fontSize: "12px",
          },
          "& .MuiDataGrid-columnSeparator--resizable": {
            color: `${tokens.blueAccent[100]} !important`,
          }
        }}>

          <DataGrid
            rows={data}
            columns={columns}
            loading={loading}
            checkboxSelection
            slots={{ toolbar: GridToolbar }}
            sx={{
                  "& .MuiDataGrid-toolbarContainer": {
                    display: "flex",
                    justifyContent: "space-between",
                  },
                  // Styling for all buttons within the DataGrid
                  "& .MuiButton-root": {
                    padding: "8px 15px", // Adjust padding for all button
                    textTransform: "none", // Keep text in normal case
                  },
                  "& .MuiButton-text": {
                    color: tokens.blueAccent[400], // Text color for text buttons
                  },
            }}
          />
          

        </Box>
      </Box>

      {/* EDIT SUPPLIER */}

      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>

        <DialogTitle
          style={{background:tokens.redAccent[200], color: tokens.blueAccent[400], fontSize: "20px"}}
        >
            Edit Supplier
        </DialogTitle>

        <DialogContent>
          <Box mt="10px" gap="10px" display="flex" justifyContent="center">
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={editSupplier?.name || ""}
            onChange={(e) =>
              setEditSupplier({ ...editSupplier, name: e.target.value })
            }
          />

          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={editSupplier?.email || ""}
            onChange={(e) =>
              setEditSupplier({ ...editSupplier, email: e.target.value })
            }
          />
          </Box>
          <Box mt="10px" gap="10px" display="flex" justifyContent="center">
          <TextField
            label="Phone"
            fullWidth
            margin="normal"
            value={editSupplier?.phone || ""}
            onChange={(e) =>
              setEditSupplier({ ...editSupplier, phone: e.target.value })
            }
          />

          <TextField
            label="Address"
            fullWidth
            margin="normal"
            value={editSupplier?.address || ""}
            onChange={(e) =>
              setEditSupplier({ ...editSupplier, address: e.target.value })
            }
          />
          </Box>

        </DialogContent>

        <DialogActions style={{background:tokens.redAccent[200]}}>
          <Button 
           style={{color: tokens.blueAccent[400]}}
           onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button 
          style={{color: tokens.blueAccent[400]}}
          onClick={handleSave}>Save</Button>
        </DialogActions>

      </Dialog> 

      {/* ADD SUPPLIER */}

      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>

        <DialogTitle style={{background:tokens.redAccent[200], color: tokens.blueAccent[400], fontSize: "20px"}}>
            Add Supplier
        </DialogTitle>

        <DialogContent>
            <Box mt="10px" gap="10px" display="flex" justifyContent="center">
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newSupplier.name}
            onChange={(e) =>
              setNewSupplier({ ...newSupplier, name: e.target.value })
            }
          />

          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={newSupplier.email}
            onChange={(e) =>
              setNewSupplier({ ...newSupplier, email: e.target.value })
            }
          />
            </Box>
            <Box gap="10px" display="flex" justifyContent="center">
          <TextField
            label="Phone"
            fullWidth
            margin="normal"
            value={newSupplier.phone}
            onChange={(e) =>
              setNewSupplier({ ...newSupplier, phone: e.target.value })
            }
          />

          <TextField
            label="Address"
            fullWidth
            margin="normal"
            value={newSupplier.address}
            onChange={(e) =>
              setNewSupplier({ ...newSupplier, address: e.target.value })
            }
          />
            </Box>

        </DialogContent>

        <DialogActions  style={{background:tokens.redAccent[200]}}>
          <Button 
           style={{color: tokens.blueAccent[400]}}
           onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
          <Button 
          style={{color: tokens.blueAccent[400]}}
          onClick={handleAddSupplier}>Save</Button>
        </DialogActions>

      </Dialog>

    </>
  );
};

export default Suppliers;